import { FC, ComponentType, ReactNode, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IconChartLine, IconTrophy, IconActivity, IconArrowsDiff, IconRoad, IconChartHistogram, IconBellRinging, IconTrendingUp, IconTrendingDown, IconArrowRight } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Heading,
  Text,
  Flex,
  HStack,
  Spinner,
  Select,
  SimpleGrid,
  Icon,
} from "@chakra-ui/react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  Cell,
  LabelList,
  ReferenceLine,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import { usePageTitle } from "@Hooks/usePageTitle";
import { useAcesso } from "@Hooks/useAcesso";
import { Button } from "@Components/Button";
import { AcessoNegadoModal } from "@Components/AcessoNegadoModal";
import { getPainelPrecos, getRankingUf, getMovimentoUf, getDispersaoUf, getAlertasPrecos, SemanaPreco, UfPreco, MovimentoUf as MovimentoUfItem, DispersaoUf as DispersaoUfItem, AlertaMicro } from "@Api/estudo";

// Paleta enxuta (Storytelling com Dados): cinza p/ contexto, vermelho RF só na mensagem.
const RF = "#D3232E";
const CINZA = "#B4B4B4";
const INK = "#1A1A1E";
const SUB = "#6C6C74";

const brl = (v: number | null, dec = 2) =>
  v == null ? "—" : `R$ ${v.toLocaleString("pt-BR", { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;
const brlDelta = (v: number | null) =>
  v == null ? "—" : `${v < 0 ? "−" : "+"}R$ ${Math.abs(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const ddmm = (iso: string) => {
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
};

// KPI "número grande" — o valor é o herói; rótulo pequeno em cima, nota embaixo.
const Kpi: FC<{ rotulo: string; valor: string; nota?: string; cor?: string; grande?: boolean }> = ({
  rotulo,
  valor,
  nota,
  cor = INK,
  grande,
}) => (
  <Box bg="neutrals.surface.surface-0" border="1px solid" borderColor="neutrals.stroke.soft-200" borderRadius="12px" p="18px">
    <Text fontSize="11px" letterSpacing="0.06em" textTransform="uppercase" color={SUB} fontWeight="600">
      {rotulo}
    </Text>
    <Text mt="4px" fontFamily="heading" fontWeight="800" lineHeight="1.1" color={cor} fontSize={grande ? "34px" : "22px"}>
      {valor}
    </Text>
    {nota && (
      <Text mt="2px" fontSize="12px" color={SUB}>
        {nota}
      </Text>
    )}
  </Box>
);

// Cabeçalho padrão do painel: título = nome do menu; abaixo, a frase-conclusão (subtítulo)
// e a linha de metadados. `controle` (opcional) = seletor de segmento à direita.
const CabecalhoPainel: FC<{ titulo: string; conclusao: string; meta: ReactNode; controle?: ReactNode }> = ({
  titulo,
  conclusao,
  meta,
  controle,
}) => (
  <Flex justify="space-between" align="flex-end" wrap="wrap" gap={3} mb="18px">
    <Box maxW="680px">
      <Heading fontSize="24px" fontFamily="heading" color={INK}>{titulo}</Heading>
      <Text mt="4px" fontSize="15px" fontWeight="600" color={INK}>{conclusao}</Text>
      <Text mt="2px" fontSize="13px" color={SUB}>{meta}</Text>
    </Box>
    {controle}
  </Flex>
);

// Rodapé "Sobre esta tela": explica, em linguagem simples, o que o painel mostra e para quê.
const SobrePainel: FC<{ children: ReactNode }> = ({ children }) => (
  <Box mt="22px" p="16px 18px" bg="neutrals.surface.surface-0" border="1px solid" borderColor="neutrals.stroke.soft-200" borderRadius="12px">
    <Text fontSize="11px" letterSpacing="0.06em" textTransform="uppercase" color={SUB} fontWeight="700" mb="6px">
      Sobre esta tela
    </Text>
    <Text fontSize="13px" color={SUB} maxW="80ch" lineHeight="1.6">{children}</Text>
  </Box>
);

const SegmentoSelect: FC<{ value: string; onChange: (v: "todos" | "cidade" | "rodovia") => void }> = ({ value, onChange }) => (
  <Box>
    <Text fontSize="11px" color={SUB} mb="4px">Segmento</Text>
    <Select size="sm" maxW="170px" value={value} onChange={(e) => onChange(e.target.value as "todos" | "cidade" | "rodovia")}>
      <option value="todos">Cidade + rodovia</option>
      <option value="cidade">Cidade</option>
      <option value="rodovia">Rodovia</option>
    </Select>
  </Box>
);

const RotuloPonto: FC<any> = ({ x, y, value, index, dataLength }) => {
  if (index !== dataLength - 1 || value == null) return null; // rotula só o último ponto (rotulagem direta)
  return (
    <text x={x + 8} y={y} dy={4} fill={RF} fontSize={14} fontWeight={700}>
      {brl(value)}
    </text>
  );
};

const PainelNacional: FC = () => {
  const [tipo, setTipo] = useState<"todos" | "cidade" | "rodovia">("rodovia");
  const { data, isLoading, isError } = useQuery({
    queryKey: ["precos-painel", tipo],
    queryFn: () => getPainelPrecos({ tipo }),
    placeholderData: (prev) => prev,
  });

  if (isLoading && !data) {
    return (
      <Flex align="center" justify="center" py={16} gap={3}>
        <Spinner color={RF} size="sm" />
        <Text color={SUB} fontFamily="body">Carregando preços…</Text>
      </Flex>
    );
  }
  if (isError || !data) {
    return <Text py={10} color={SUB}>Não foi possível carregar os preços. Tente novamente.</Text>;
  }

  const chart = data.semanas.map((s: SemanaPreco) => ({ label: ddmm(s.semana), rf: s.rf, anp: s.anp }));
  const dataLength = chart.length;
  const rfAtual = data.ultima?.rf ?? null;
  // Título-conclusão: a mensagem, não o rótulo.
  const titulo =
    rfAtual != null && data.vsAnp != null
      ? `Diesel S10 a ${brl(rfAtual)}/L — ${brlDelta(data.vsAnp)}/L vs. ANP`
      : "Diesel S10 — evolução do preço na Rede Frota";

  return (
    <Box>
      <CabecalhoPainel
        titulo="Painel nacional"
        conclusao={titulo}
        meta={<>Preço praticado (ponderado por litros) · atualizado até {data.atualizadoAte ? ddmm(data.atualizadoAte) : "—"} · {data.meta.fonte}</>}
        controle={<SegmentoSelect value={tipo} onChange={setTipo} />}
      />

      <SimpleGrid columns={{ base: 2, md: 4 }} spacing="14px" mb="22px">
        <Kpi rotulo="Preço médio Rede Frota" valor={`${brl(rfAtual)}/L`} nota="última semana" cor={RF} grande />
        <Kpi rotulo="vs. ANP" valor={`${brlDelta(data.vsAnp)}/L`} nota={data.vsAnp != null && data.vsAnp < 0 ? "Rede Frota mais barato" : "Rede Frota acima da ANP"} cor={data.vsAnp != null && data.vsAnp < 0 ? "#15794B" : RF} />
        <Kpi rotulo="Em 4 semanas" valor={`${brlDelta(data.delta4sem)}/L`} nota={data.delta4sem != null && data.delta4sem > 0 ? "em alta" : "em queda"} cor={data.delta4sem != null && data.delta4sem > 0 ? RF : "#15794B"} />
        <Kpi rotulo="IPRF (índice)" valor={brl(data.ultima?.iprf ?? null, 3)} nota="referência do dia" />
      </SimpleGrid>

      {/* Linha SWD: RF em vermelho (a mensagem), ANP em cinza tracejado (contexto). Sem grid. */}
      <Box bg="neutrals.surface.surface-0" border="1px solid" borderColor="neutrals.stroke.soft-200" borderRadius="12px" p="18px">
        <Text fontSize="12px" letterSpacing="0.06em" textTransform="uppercase" color={SUB} fontWeight="600" mb="10px">
          Evolução semanal — Rede Frota vs. ANP
        </Text>
        <Box h="300px">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chart} margin={{ top: 10, right: 64, bottom: 4, left: 4 }}>
              <XAxis dataKey="label" tickLine={false} axisLine={{ stroke: "#E7E4DE" }} tick={{ fill: SUB, fontSize: 11 }} interval="preserveStartEnd" minTickGap={24} />
              <YAxis domain={["dataMin - 0.15", "dataMax + 0.15"]} tickLine={false} axisLine={false} tick={{ fill: SUB, fontSize: 11 }} tickFormatter={(v) => v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} width={44} />
              <Tooltip
                formatter={(v: number, name: string) => [brl(v), name === "rf" ? "Rede Frota" : "ANP"]}
                labelFormatter={(l) => `Semana ${l}`}
                contentStyle={{ borderRadius: 8, border: "1px solid #E7E4DE", fontSize: 12 }}
              />
              <Line type="monotone" dataKey="anp" stroke={CINZA} strokeWidth={1.5} strokeDasharray="5 4" dot={false} isAnimationActive={false} connectNulls />
              <Line
                type="monotone"
                dataKey="rf"
                stroke={RF}
                strokeWidth={2.6}
                dot={{ r: 0 }}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
                label={(props: any) => <RotuloPonto {...props} dataLength={dataLength} />}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
        <HStack spacing={5} mt="8px">
          <HStack spacing={2}><Box w="14px" h="3px" bg={RF} borderRadius="2px" /><Text fontSize="12px" color={SUB}>Rede Frota</Text></HStack>
          <HStack spacing={2}><Box w="14px" h="0" borderTop="2px dashed" borderColor={CINZA} /><Text fontSize="12px" color={SUB}>ANP</Text></HStack>
        </HStack>
      </Box>
      <SobrePainel>
        Preço médio do diesel S-10 praticado na Rede Frota (ponderado por litros), comparado com a média
        de referência da ANP e resumido no índice IPRF, com a evolução das últimas semanas. Use para
        acompanhar o movimento geral do preço no país e a posição da Rede Frota frente à ANP.
      </SobrePainel>
    </Box>
  );
};

const VERDE = "#15794B";

// Rótulo p/ barra divergente: sempre no extremo EXTERNO (positivo→direita, negativo→esquerda),
// nunca colado no eixo zero (evita sobreposição das barras negativas).
const RotuloDivergente: FC<any> = ({ x, y, width, height, value }) => {
  if (value == null) return null;
  const pos = value >= 0;
  // width pode vir negativo p/ barras negativas — normaliza pelas bordas reais.
  const left = Math.min(x, x + width);
  const right = Math.max(x, x + width);
  const px = pos ? right + 5 : left - 5;
  return (
    <text x={px} y={y + height / 2} dy={4} textAnchor={pos ? "start" : "end"} fill={INK} fontSize={11}>
      {brlDelta(value)}
    </text>
  );
};

// Uma linha do ranking: rótulo UF + faixa menor→maior do estado (pontas + linha central
// mais escura), linha da média nacional e o ponto do preço médio da UF (vermelho/verde).
const RankingDotRow: FC<{ it: UfPreco; media: number; escMin: number; escMax: number }> = ({ it, media, escMin, escMax }) => {
  const span = escMax - escMin || 1;
  const pct = (v: number) => `${((v - escMin) / span) * 100}%`;
  const acima = it.rf >= media;
  const cor = acima ? RF : VERDE;
  const min = it.min ?? it.rf;
  const max = it.max ?? it.rf;
  return (
    <Flex align="center" gap={3} py="6px">
      <Text w="26px" flexShrink={0} fontSize="12px" fontWeight="700" color={INK} textAlign="right">{it.uf}</Text>
      <Box position="relative" flex={1} h="18px">
        {/* trilho de fundo */}
        <Box position="absolute" top="50%" left={0} right={0} h="1px" bg="neutrals.stroke.soft-200" transform="translateY(-50%)" />
        {/* faixa menor→maior: barra CLARA com pontas arredondadas */}
        <Box position="absolute" top="50%" left={pct(min)} w={`${((max - min) / span) * 100}%`} h="6px" bg={CINZA} opacity={0.45} borderRadius="3px" transform="translateY(-50%)" title={`faixa ${brl(min)}–${brl(max)}`} />
        {/* média nacional (referência): traço escuro */}
        <Box position="absolute" top="50%" left={pct(media)} w="2px" h="13px" bg={INK} borderRadius="1px" transform="translate(-1px,-50%)" title={`média nacional ${brl(media)}`} />
        {/* ponto do preço médio da UF */}
        <Box position="absolute" top="50%" left={pct(it.rf)} boxSize="11px" bg={cor} borderRadius="full" border="2px solid white" transform="translate(-5.5px,-50%)" title={`${it.uf} média ${brl(it.rf)}`} />
      </Box>
      <Text w="132px" flexShrink={0} fontSize="12px" color={SUB} textAlign="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
        <Text as="span" fontWeight="700" color={cor}>{brl(it.rf)}</Text>
        <Text as="span" fontSize="11px"> · {brl(min).replace("R$ ", "")}–{brl(max).replace("R$ ", "")}</Text>
      </Text>
    </Flex>
  );
};

const RankingUf: FC = () => {
  const [tipo, setTipo] = useState<"todos" | "cidade" | "rodovia">("rodovia");
  const { data, isLoading, isError } = useQuery({
    queryKey: ["precos-ranking", tipo],
    queryFn: () => getRankingUf({ tipo }),
    placeholderData: (prev) => prev,
  });
  if (isLoading && !data) {
    return (
      <Flex align="center" justify="center" py={16} gap={3}>
        <Spinner color={RF} size="sm" /><Text color={SUB}>Carregando…</Text>
      </Flex>
    );
  }
  if (isError || !data || data.itens.length === 0) {
    return <Text py={10} color={SUB}>Sem dados de ranking para os filtros atuais.</Text>;
  }
  const media = data.mediaNacional ?? 0;
  const itens = data.itens;
  const maior = itens[0];
  const menor = itens[itens.length - 1];
  const escMin = Math.min(media, ...itens.map((x) => x.min ?? x.rf));
  const escMax = Math.max(media, ...itens.map((x) => x.max ?? x.rf));
  const pct = (v: number) => (media ? `${v - media >= 0 ? "+" : "−"}${(Math.abs(v - media) / media * 100).toFixed(1)}%` : "—");

  return (
    <Box>
      <CabecalhoPainel
        titulo="Ranking por UF"
        conclusao={`${maior.uf} tem o diesel mais caro; ${menor.uf}, o mais barato`}
        meta={<>Preço Rede Frota por estado · semana de {data.semana ? ddmm(data.semana) : "—"} · média nacional {brl(media)}/L · {data.meta.fonte}</>}
        controle={<SegmentoSelect value={tipo} onChange={setTipo} />}
      />

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing="14px" mb="18px">
        <Kpi rotulo={`Maior preço · ${maior.uf}`} valor={`${brl(maior.rf)}/L`} nota={`${pct(maior.rf)} vs. média Rede Frota`} cor={RF} />
        <Kpi rotulo={`Menor preço · ${menor.uf}`} valor={`${brl(menor.rf)}/L`} nota={`${pct(menor.rf)} vs. média Rede Frota`} cor={VERDE} />
      </SimpleGrid>

      {/* Dot plot (SWD): preço é ponto, não barra — evita distorção de eixo cortado.
          Linha da média como referência; ponto vermelho acima da média, verde abaixo. */}
      <Box bg="neutrals.surface.surface-0" border="1px solid" borderColor="neutrals.stroke.soft-200" borderRadius="12px" p="18px">
        <Text fontSize="12px" letterSpacing="0.06em" textTransform="uppercase" color={SUB} fontWeight="600" mb="12px">
          Preço Rede Frota por UF
        </Text>
        <Box>
          {itens.map((it: UfPreco) => (
            <RankingDotRow key={it.uf} it={it} media={media} escMin={escMin} escMax={escMax} />
          ))}
        </Box>
        <HStack spacing={5} mt="12px" flexWrap="wrap">
          <HStack spacing={2}><Box w="16px" h="6px" bg={CINZA} opacity={0.45} borderRadius="3px" /><Text fontSize="12px" color={SUB}>faixa menor–maior</Text></HStack>
          <HStack spacing={2}><Box w="2px" h="12px" bg={INK} borderRadius="1px" /><Text fontSize="12px" color={SUB}>média nacional ({brl(media)}/L)</Text></HStack>
          <HStack spacing={2}><Box boxSize="9px" bg={RF} borderRadius="full" /><Text fontSize="12px" color={SUB}>UF acima da média</Text></HStack>
          <HStack spacing={2}><Box boxSize="9px" bg={VERDE} borderRadius="full" /><Text fontSize="12px" color={SUB}>UF abaixo da média</Text></HStack>
        </HStack>
      </Box>
      <SobrePainel>
        Compara o preço médio da Rede Frota entre os estados na última semana, mostrando a faixa
        (menor→maior preço) dentro de cada UF e a média nacional como referência. Use para ver onde o
        diesel está mais caro ou mais barato e priorizar negociações por região.
      </SobrePainel>
    </Box>
  );
};

const MovimentoUf: FC = () => {
  const [tipo, setTipo] = useState<"todos" | "cidade" | "rodovia">("rodovia");
  const { data, isLoading, isError } = useQuery({
    queryKey: ["precos-movimento", tipo],
    queryFn: () => getMovimentoUf({ tipo }),
    placeholderData: (prev) => prev,
  });
  if (isLoading && !data) {
    return (
      <Flex align="center" justify="center" py={16} gap={3}>
        <Spinner color={RF} size="sm" /><Text color={SUB}>Carregando…</Text>
      </Flex>
    );
  }
  if (isError || !data || data.itens.length === 0) {
    return <Text py={10} color={SUB}>Sem movimento de preço para os filtros atuais.</Text>;
  }
  // Já vem ordenado por delta desc no backend. Maior alta = 1º; maior queda = último.
  const itens = data.itens;
  const alta = itens[0];
  const queda = itens[itens.length - 1];
  // Título-conclusão: o maior movimento da semana é a história.
  const titulo =
    alta && queda && alta.delta > 0
      ? `${alta.uf} subiu ${brlDelta(alta.delta)}/L; ${queda.uf} recuou ${brlDelta(queda.delta)}/L`
      : "Movimento do diesel na semana por estado";

  return (
    <Box>
      <CabecalhoPainel
        titulo="Movimento do IPRF"
        conclusao={titulo}
        meta={<>Variação do preço Rede Frota vs. a semana anterior · {data.semana ? ddmm(data.semana) : "—"} vs. {data.semanaAnterior ? ddmm(data.semanaAnterior) : "—"} · {data.meta.fonte}</>}
        controle={<SegmentoSelect value={tipo} onChange={setTipo} />}
      />

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing="14px" mb="18px">
        <Kpi rotulo={`Maior alta · ${alta.uf}`} valor={`${brlDelta(alta.delta)}/L`} nota={`para ${brl(alta.rfUlt)}/L`} cor={RF} />
        <Kpi rotulo={`Maior queda · ${queda.uf}`} valor={`${brlDelta(queda.delta)}/L`} nota={`para ${brl(queda.rfUlt)}/L`} cor={VERDE} />
      </SimpleGrid>

      {/* Barra divergente SWD: altas à direita (vermelho), quedas à esquerda (verde); zero no centro. */}
      <Box bg="neutrals.surface.surface-0" border="1px solid" borderColor="neutrals.stroke.soft-200" borderRadius="12px" p="18px">
        <Text fontSize="12px" letterSpacing="0.06em" textTransform="uppercase" color={SUB} fontWeight="600" mb="10px">
          Variação semanal por UF (R$/L)
        </Text>
        <Box h={`${Math.max(260, itens.length * 22)}px`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={itens} layout="vertical" margin={{ top: 4, right: 60, bottom: 4, left: 60 }} barCategoryGap={3}>
              <XAxis type="number" domain={["dataMin - 0.05", "dataMax + 0.05"]} hide />
              <YAxis type="category" dataKey="uf" width={30} tickLine={false} axisLine={false} tick={{ fill: SUB, fontSize: 11 }} />
              <Tooltip formatter={(v: number) => [brlDelta(v), "Variação"]} labelFormatter={(l) => `UF ${l}`} contentStyle={{ borderRadius: 8, border: "1px solid #E7E4DE", fontSize: 12 }} />
              <ReferenceLine x={0} stroke={SUB} />
              <Bar dataKey="delta" radius={2} isAnimationActive={false}>
                {itens.map((it: MovimentoUfItem) => (
                  <Cell key={it.uf} fill={it.delta >= 0 ? RF : VERDE} fillOpacity={0.9} />
                ))}
                <LabelList dataKey="delta" content={RotuloDivergente} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
        <Text fontSize="12px" color={SUB} mt="8px">
          Vermelho = preço subiu na semana; verde = preço caiu. Linha central = sem variação.
        </Text>
      </Box>
      <SobrePainel>
        Mostra quanto o preço da Rede Frota subiu ou caiu em cada estado na última semana em relação à
        anterior (variação em R$/L). Use para identificar rapidamente onde houve alta ou queda recente e
        reagir com os clientes daquela região.
      </SobrePainel>
    </Box>
  );
};

const GapAnp: FC = () => {
  const [tipo, setTipo] = useState<"todos" | "cidade" | "rodovia">("rodovia");
  const { data, isLoading, isError } = useQuery({
    queryKey: ["precos-ranking", tipo], // mesmo cache do Ranking — reusa o endpoint
    queryFn: () => getRankingUf({ tipo }),
    placeholderData: (prev) => prev,
  });
  if (isLoading && !data) {
    return (
      <Flex align="center" justify="center" py={16} gap={3}>
        <Spinner color={RF} size="sm" /><Text color={SUB}>Carregando…</Text>
      </Flex>
    );
  }
  const comAnp = (data?.itens ?? []).filter((x) => x.vsAnp != null) as (UfPreco & { vsAnp: number })[];
  if (isError || comAnp.length === 0) {
    return <Text py={10} color={SUB}>Sem comparação Rede Frota × ANP para os filtros atuais.</Text>;
  }
  // Ordena por gap: mais negativo (Rede Frota mais barato) primeiro.
  const itens = [...comAnp].sort((a, b) => a.vsAnp - b.vsAnp);
  const abaixo = itens.filter((x) => x.vsAnp < 0);
  const melhor = itens[0]; // RF mais abaixo da ANP
  const pior = itens[itens.length - 1]; // RF mais acima da ANP
  const titulo =
    `Rede Frota está abaixo da ANP em ${abaixo.length} de ${itens.length} estados`;

  return (
    <Box>
      <CabecalhoPainel
        titulo="Rede Frota × ANP por UF"
        conclusao={titulo}
        meta={<>Diferença do preço Rede Frota para a média ANP, por estado · semana de {data?.semana ? ddmm(data.semana) : "—"} · {data?.meta.fonte}</>}
        controle={<SegmentoSelect value={tipo} onChange={setTipo} />}
      />

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing="14px" mb="18px">
        <Kpi rotulo={`Melhor vantagem · ${melhor.uf}`} valor={`${brlDelta(melhor.vsAnp)}/L`} nota="Rede Frota abaixo da ANP" cor={VERDE} />
        <Kpi rotulo={`Maior defasagem · ${pior.uf}`} valor={`${brlDelta(pior.vsAnp)}/L`} nota={pior.vsAnp >= 0 ? "Rede Frota acima da ANP" : "Rede Frota abaixo da ANP"} cor={pior.vsAnp >= 0 ? RF : VERDE} />
      </SimpleGrid>

      {/* Barra divergente: Rede Frota abaixo da ANP (verde, à esquerda) vs. acima (vermelho). Zero = paridade. */}
      <Box bg="neutrals.surface.surface-0" border="1px solid" borderColor="neutrals.stroke.soft-200" borderRadius="12px" p="18px">
        <Text fontSize="12px" letterSpacing="0.06em" textTransform="uppercase" color={SUB} fontWeight="600" mb="10px">
          Rede Frota − ANP por UF (R$/L)
        </Text>
        <Box h={`${Math.max(260, itens.length * 22)}px`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={itens} layout="vertical" margin={{ top: 4, right: 60, bottom: 4, left: 60 }} barCategoryGap={3}>
              <XAxis type="number" domain={["dataMin - 0.05", "dataMax + 0.05"]} hide />
              <YAxis type="category" dataKey="uf" width={30} tickLine={false} axisLine={false} tick={{ fill: SUB, fontSize: 11 }} />
              <Tooltip formatter={(v: number) => [brlDelta(v), "Rede Frota − ANP"]} labelFormatter={(l) => `UF ${l}`} contentStyle={{ borderRadius: 8, border: "1px solid #E7E4DE", fontSize: 12 }} />
              <ReferenceLine x={0} stroke={SUB} />
              <Bar dataKey="vsAnp" radius={2} isAnimationActive={false}>
                {itens.map((it) => (
                  <Cell key={it.uf} fill={it.vsAnp < 0 ? VERDE : RF} fillOpacity={0.9} />
                ))}
                <LabelList dataKey="vsAnp" content={RotuloDivergente} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
        <Text fontSize="12px" color={SUB} mt="8px">
          Verde = Rede Frota mais barato que a ANP (vantagem); vermelho = Rede Frota acima da ANP. Linha central = paridade.
        </Text>
      </Box>
      <SobrePainel>
        Mostra, por estado, a diferença entre o preço da Rede Frota e a média de referência da ANP:
        verde onde a Rede Frota está mais barata (vantagem competitiva) e vermelho onde está acima. Use
        para argumentar competitividade e priorizar os estados onde a Rede Frota mais ganha da ANP.
      </SobrePainel>
    </Box>
  );
};

const AZUL = "#2D6BB0";

const CidadeRodovia: FC = () => {
  const qCidade = useQuery({ queryKey: ["precos-painel", "cidade"], queryFn: () => getPainelPrecos({ tipo: "cidade" }), placeholderData: (p) => p });
  const qRodovia = useQuery({ queryKey: ["precos-painel", "rodovia"], queryFn: () => getPainelPrecos({ tipo: "rodovia" }), placeholderData: (p) => p });
  const carregando = (qCidade.isLoading && !qCidade.data) || (qRodovia.isLoading && !qRodovia.data);
  if (carregando) {
    return (
      <Flex align="center" justify="center" py={16} gap={3}>
        <Spinner color={RF} size="sm" /><Text color={SUB}>Carregando…</Text>
      </Flex>
    );
  }
  const cid = qCidade.data;
  const rod = qRodovia.data;
  if (!cid || !rod) {
    return <Text py={10} color={SUB}>Não foi possível comparar cidade e rodovia.</Text>;
  }
  // Une as duas séries por semana (label dd/mm).
  const porLabel = new Map<string, { label: string; cidade: number | null; rodovia: number | null }>();
  for (const s of cid.semanas) porLabel.set(s.semana, { label: ddmm(s.semana), cidade: s.rf, rodovia: null });
  for (const s of rod.semanas) {
    const cur = porLabel.get(s.semana) ?? { label: ddmm(s.semana), cidade: null, rodovia: null };
    cur.rodovia = s.rf;
    porLabel.set(s.semana, cur);
  }
  const chart = Array.from(porLabel.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([, v]) => v);
  const rfCid = cid.ultima?.rf ?? null;
  const rfRod = rod.ultima?.rf ?? null;
  const dif = rfCid != null && rfRod != null ? +(rfRod - rfCid).toFixed(3) : null;
  const rodMaisCara = dif != null && dif > 0;
  const titulo =
    dif != null
      ? `Na rodovia o diesel está ${brlDelta(Math.abs(dif))}/L ${rodMaisCara ? "mais caro" : "mais barato"} que na cidade`
      : "Diesel na cidade e na rodovia";

  return (
    <Box>
      <CabecalhoPainel
        titulo="Cidade × Rodovia"
        conclusao={titulo}
        meta={<>Preço médio Rede Frota por segmento · atualizado até {rod.atualizadoAte ? ddmm(rod.atualizadoAte) : "—"} · {rod.meta.fonte}</>}
      />

      <SimpleGrid columns={{ base: 2, md: 3 }} spacing="14px" mb="20px">
        <Kpi rotulo="Rodovia" valor={`${brl(rfRod)}/L`} nota="última semana" cor={RF} grande />
        <Kpi rotulo="Cidade" valor={`${brl(rfCid)}/L`} nota="última semana" cor={AZUL} grande />
        <Kpi rotulo="Diferença" valor={`${brlDelta(dif)}/L`} nota={rodMaisCara ? "rodovia mais cara" : "cidade mais cara"} />
      </SimpleGrid>

      {/* Duas linhas comparáveis: rodovia (vermelho RF) vs. cidade (azul). Sem grid. */}
      <Box bg="neutrals.surface.surface-0" border="1px solid" borderColor="neutrals.stroke.soft-200" borderRadius="12px" p="18px">
        <Text fontSize="12px" letterSpacing="0.06em" textTransform="uppercase" color={SUB} fontWeight="600" mb="10px">
          Evolução semanal — rodovia vs. cidade
        </Text>
        <Box h="300px">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chart} margin={{ top: 10, right: 64, bottom: 4, left: 4 }}>
              <XAxis dataKey="label" tickLine={false} axisLine={{ stroke: "#E7E4DE" }} tick={{ fill: SUB, fontSize: 11 }} interval="preserveStartEnd" minTickGap={24} />
              <YAxis domain={["dataMin - 0.15", "dataMax + 0.15"]} tickLine={false} axisLine={false} tick={{ fill: SUB, fontSize: 11 }} tickFormatter={(v) => v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} width={44} />
              <Tooltip formatter={(v: number, name: string) => [brl(v), name === "rodovia" ? "Rodovia" : "Cidade"]} labelFormatter={(l) => `Semana ${l}`} contentStyle={{ borderRadius: 8, border: "1px solid #E7E4DE", fontSize: 12 }} />
              <Line type="monotone" dataKey="rodovia" stroke={RF} strokeWidth={2.6} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} connectNulls />
              <Line type="monotone" dataKey="cidade" stroke={AZUL} strokeWidth={2.2} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </Box>
        <HStack spacing={5} mt="8px">
          <HStack spacing={2}><Box w="14px" h="3px" bg={RF} borderRadius="2px" /><Text fontSize="12px" color={SUB}>Rodovia</Text></HStack>
          <HStack spacing={2}><Box w="14px" h="3px" bg={AZUL} borderRadius="2px" /><Text fontSize="12px" color={SUB}>Cidade</Text></HStack>
        </HStack>
      </Box>
      <SobrePainel>
        Compara o preço médio do diesel na Rede Frota entre abastecimento em cidade e em rodovia, com a
        evolução das últimas semanas. Use para entender a diferença de preço por tipo de praça e orientar
        o cliente sobre onde abastecer.
      </SobrePainel>
    </Box>
  );
};

// Uma linha do range-plot: faixa min→max (cinza) com mediana (traço escuro) e média RF (ponto vermelho).
const LinhaDispersao: FC<{ it: DispersaoUfItem; escMin: number; escMax: number }> = ({ it, escMin, escMax }) => {
  const span = escMax - escMin || 1;
  const pos = (v: number) => `${((v - escMin) / span) * 100}%`;
  const left = ((it.min - escMin) / span) * 100;
  const width = ((it.max - it.min) / span) * 100;
  return (
    <Flex align="center" gap={3} py="5px">
      <Text w="26px" flexShrink={0} fontSize="12px" fontWeight="700" color={INK} textAlign="right">{it.uf}</Text>
      <Box position="relative" flex={1} h="18px">
        {/* trilho */}
        <Box position="absolute" top="50%" left={0} right={0} h="1px" bg="neutrals.stroke.soft-200" transform="translateY(-50%)" />
        {/* faixa min→max */}
        <Box position="absolute" top="50%" left={`${left}%`} w={`${width}%`} h="6px" bg={CINZA} opacity={0.55} borderRadius="3px" transform="translateY(-50%)" />
        {/* mediana */}
        <Box position="absolute" top="50%" left={pos(it.mediana)} w="2px" h="14px" bg={INK} transform="translate(-1px,-50%)" title={`mediana ${brl(it.mediana)}`} />
        {/* média ponderada RF */}
        <Box position="absolute" top="50%" left={pos(it.mediaPond)} boxSize="9px" bg={RF} borderRadius="full" border="1.5px solid white" transform="translate(-4.5px,-50%)" title={`média Rede Frota ${brl(it.mediaPond)}`} />
      </Box>
      <Text w="86px" flexShrink={0} fontSize="11px" color={SUB} textAlign="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {brl(it.min)}–{brl(it.max).replace("R$ ", "")}
      </Text>
    </Flex>
  );
};

const EstatisticaRegional: FC = () => {
  const [tipo, setTipo] = useState<"todos" | "cidade" | "rodovia">("rodovia");
  const { data, isLoading, isError } = useQuery({
    queryKey: ["precos-dispersao", tipo],
    queryFn: () => getDispersaoUf({ tipo }),
    placeholderData: (prev) => prev,
  });
  if (isLoading && !data) {
    return (
      <Flex align="center" justify="center" py={16} gap={3}>
        <Spinner color={RF} size="sm" /><Text color={SUB}>Carregando…</Text>
      </Flex>
    );
  }
  if (isError || !data || data.itens.length === 0) {
    return <Text py={10} color={SUB}>Sem dados de dispersão para os filtros atuais.</Text>;
  }
  const itens = data.itens; // já ordenado por amplitude desc
  const escMin = Math.min(...itens.map((x) => x.min));
  const escMax = Math.max(...itens.map((x) => x.max));
  const maior = itens[0];
  const titulo = `${maior.uf} tem a maior variação interna: de ${brl(maior.min)} a ${brl(maior.max)}/L`;

  return (
    <Box>
      <CabecalhoPainel
        titulo="Estatística regional"
        conclusao={titulo}
        meta={<>Preço Rede Frota entre microrregiões de cada estado · semana de {data.semana ? ddmm(data.semana) : "—"} · {data.meta.fonte}</>}
        controle={<SegmentoSelect value={tipo} onChange={setTipo} />}
      />

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing="14px" mb="18px">
        <Kpi rotulo={`Maior amplitude · ${maior.uf}`} valor={`${brlDelta(maior.amplitude)}/L`} nota={`${maior.nMicro} microrregiões`} cor={RF} />
        <Kpi rotulo="Estados analisados" valor={String(itens.length)} nota="com ≥ 2 microrregiões" />
        <Kpi rotulo="Faixa nacional" valor={`${brl(escMin)}–${brl(escMax).replace("R$ ", "")}`} nota="menor e maior célula" />
      </SimpleGrid>

      <Box bg="neutrals.surface.surface-0" border="1px solid" borderColor="neutrals.stroke.soft-200" borderRadius="12px" p="18px">
        <Text fontSize="12px" letterSpacing="0.06em" textTransform="uppercase" color={SUB} fontWeight="600" mb="12px">
          Faixa de preço Rede Frota por UF (menor → maior microrregião)
        </Text>
        <Box>
          {itens.map((it) => (
            <LinhaDispersao key={it.uf} it={it} escMin={escMin} escMax={escMax} />
          ))}
        </Box>
        <HStack spacing={5} mt="12px" flexWrap="wrap">
          <HStack spacing={2}><Box w="16px" h="6px" bg={CINZA} opacity={0.55} borderRadius="3px" /><Text fontSize="12px" color={SUB}>faixa mín–máx</Text></HStack>
          <HStack spacing={2}><Box w="2px" h="12px" bg={INK} /><Text fontSize="12px" color={SUB}>mediana</Text></HStack>
          <HStack spacing={2}><Box boxSize="9px" bg={RF} borderRadius="full" /><Text fontSize="12px" color={SUB}>média Rede Frota (ponderada)</Text></HStack>
        </HStack>
      </Box>
      <SobrePainel>
        Mostra a dispersão do preço da Rede Frota dentro de cada estado — menor, mediana e maior preço
        entre as microrregiões — ordenada pela maior variação interna. Use para achar estados onde há
        mais diferença de preço entre regiões e, portanto, mais oportunidade de negociação regional.
      </SobrePainel>
    </Box>
  );
};

// Uma lista de alertas (altas ou quedas). Cabeçalho colorido; linhas compactas.
const ListaAlertas: FC<{ titulo: string; icone: ComponentType<any>; cor: string; itens: AlertaMicro[] }> = ({ titulo, icone, cor, itens }) => (
  <Box bg="neutrals.surface.surface-0" border="1px solid" borderColor="neutrals.stroke.soft-200" borderRadius="12px" overflow="hidden">
    <Flex align="center" gap={2} px="16px" py="12px" bg={cor} color="white">
      <Icon as={icone} boxSize={4} />
      <Text fontSize="13px" fontWeight="700" letterSpacing="0.02em" textTransform="uppercase">{titulo}</Text>
    </Flex>
    {itens.length === 0 ? (
      <Text px="16px" py="14px" fontSize="13px" color={SUB}>Nenhum movimento relevante nesta direção.</Text>
    ) : (
      itens.map((it, i) => (
        <Flex key={it.microKey} align="center" gap={3} px="16px" py="9px" borderTop={i === 0 ? "none" : "1px solid"} borderColor="neutrals.stroke.soft-200">
          <Text w="22px" fontSize="13px" fontWeight="700" color={cor}>{it.uf}</Text>
          <Box flex={1} minW={0}>
            <Text fontSize="13px" fontWeight="600" color={INK}>{it.microKey}</Text>
            <Text fontSize="11px" color={SUB}>{brl(it.rfPen)} → {brl(it.rfUlt)}/L · n={it.n}</Text>
          </Box>
          <Text fontSize="14px" fontWeight="800" color={cor} sx={{ fontVariantNumeric: "tabular-nums" }}>
            {brlDelta(it.delta)}
          </Text>
        </Flex>
      ))
    )}
  </Box>
);

const AlertasMovimento: FC = () => {
  const [tipo, setTipo] = useState<"todos" | "cidade" | "rodovia">("rodovia");
  const { data, isLoading, isError } = useQuery({
    queryKey: ["precos-alertas", tipo],
    queryFn: () => getAlertasPrecos({ tipo, limite: 8 }),
    placeholderData: (prev) => prev,
  });
  if (isLoading && !data) {
    return (
      <Flex align="center" justify="center" py={16} gap={3}>
        <Spinner color={RF} size="sm" /><Text color={SUB}>Carregando…</Text>
      </Flex>
    );
  }
  if (isError || !data || (data.altas.length === 0 && data.quedas.length === 0)) {
    return <Text py={10} color={SUB}>Sem alertas de movimento para os filtros atuais.</Text>;
  }
  const maiorAlta = data.altas[0];
  const maiorQueda = data.quedas[0];
  const titulo =
    maiorAlta && maiorQueda
      ? `${maiorAlta.microKey} subiu ${brlDelta(maiorAlta.delta)}/L; ${maiorQueda.microKey} recuou ${brlDelta(maiorQueda.delta)}/L`
      : maiorAlta
        ? `${maiorAlta.microKey} teve a maior alta: ${brlDelta(maiorAlta.delta)}/L`
        : `${maiorQueda.microKey} teve a maior queda: ${brlDelta(maiorQueda.delta)}/L`;

  return (
    <Box>
      <CabecalhoPainel
        titulo="Alertas de movimento"
        conclusao={titulo}
        meta={<>Maiores movimentos por microrregião · {data.semana ? ddmm(data.semana) : "—"} vs. {data.semanaAnterior ? ddmm(data.semanaAnterior) : "—"} · {data.meta.fonte}</>}
        controle={<SegmentoSelect value={tipo} onChange={setTipo} />}
      />

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing="16px">
        <ListaAlertas titulo="Maiores altas" icone={IconTrendingUp} cor={RF} itens={data.altas} />
        <ListaAlertas titulo="Maiores quedas" icone={IconTrendingDown} cor={VERDE} itens={data.quedas} />
      </SimpleGrid>
      <Text fontSize="12px" color={SUB} mt="12px">
        Cada linha é uma microrregião (UF-código). Δ = variação do preço Rede Frota na semana; n = nº de células. Amostra mínima {data.meta.amostraMinima}.
      </Text>
      <SobrePainel>
        Lista as maiores altas e as maiores quedas de preço da Rede Frota por microrregião na última
        semana. É a visão mais granular do movimento — use para agir rápido exatamente onde o preço
        subiu ou caiu mais, no nível de microrregião.
      </SobrePainel>
    </Box>
  );
};

export interface PainelDef {
  key: string;
  label: string;
  descricao: string;
  icon: ComponentType<{ size?: number | string }>;
  comp: FC;
}
// Fonte única dos painéis (home em cards, detalhe por slug e submenu da sidebar).
export const PAINEIS: PainelDef[] = [
  { key: "nacional", label: "Painel nacional", icon: IconChartLine, comp: PainelNacional, descricao: "Preço médio do diesel na Rede Frota, vs. ANP e IPRF, com a evolução semanal." },
  { key: "ranking", label: "Ranking por UF", icon: IconTrophy, comp: RankingUf, descricao: "Preço Rede Frota por estado — onde está mais caro e mais barato vs. a média." },
  { key: "movimento", label: "Movimento do IPRF", icon: IconActivity, comp: MovimentoUf, descricao: "Quais estados subiram ou recuaram o preço na última semana." },
  { key: "rf-vs-anp", label: "Rede Frota × ANP por UF", icon: IconArrowsDiff, comp: GapAnp, descricao: "Onde a Rede Frota está abaixo ou acima da média ANP, por estado." },
  { key: "cidade-rodovia", label: "Cidade × Rodovia", icon: IconRoad, comp: CidadeRodovia, descricao: "Comparação do preço médio entre abastecimento em cidade e em rodovia." },
  { key: "estatistica-regional", label: "Estatística regional", icon: IconChartHistogram, comp: EstatisticaRegional, descricao: "Dispersão do preço Rede Frota dentro de cada estado — onde há mais oportunidade." },
  { key: "alertas", label: "Alertas de movimento", icon: IconBellRinging, comp: AlertasMovimento, descricao: "Maiores altas e quedas de preço por microrregião na última semana." },
];

const PainelCard: FC<{ def: PainelDef; onClick: () => void }> = ({ def, onClick }) => (
  <Box as="button" type="button" onClick={onClick} textAlign="left" position="relative"
    bg="neutrals.surface.surface-0" border="1px solid" borderColor="neutrals.stroke.soft-200" borderRadius="12px" p="20px"
    transition="all 0.15s" _hover={{ borderColor: "primary.light.base", boxShadow: "sm" }}>
    <Icon as={IconArrowRight} boxSize={4} color="neutrals.icon.soft-400" position="absolute" top="20px" right="20px" />
    <Flex align="center" justify="center" boxSize="44px" borderRadius="10px" bg="primary.light.alpha-10" mb="14px">
      <Icon as={def.icon} boxSize={5} color="primary.light.base" />
    </Flex>
    <Text fontSize="15px" fontWeight="700" fontFamily="heading" color="neutrals.text.strong-950">{def.label}</Text>
    <Text mt="6px" fontSize="13px" color={SUB} pr="16px">{def.descricao}</Text>
  </Box>
);

export const PrecosPage: FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug?: string }>();
  const { canPrecos } = useAcesso();
  const atual = slug ? PAINEIS.find((p) => p.key === slug) : null;
  usePageTitle(atual ? `Preços · ${atual.label}` : "Inteligência de Preços");

  if (!canPrecos) {
    return (
      <AcessoNegadoModal
        isOpen
        onClose={() => navigate("/inicio")}
        titulo="Sem acesso"
        mensagem="A Inteligência de Preços é restrita a quem tem o papel de Preços ou admin. Procure o seu gestor."
        botaoLabel="Voltar"
      />
    );
  }

  // Detalhe de um painel (rota /precos/:slug).
  if (slug) {
    const Atual = (atual ?? PAINEIS[0]).comp;
    return (
      <Box>
        <Atual />
      </Box>
    );
  }

  // Home (rota /precos): grid de cards.
  return (
    <Box>
      <Heading fontSize="24px" fontFamily="heading" color={INK}>Inteligência de Preços</Heading>
      <Text mt="4px" mb="24px" fontSize="sm" color={SUB}>Selecione um painel para continuar.</Text>
      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing="20px" maxW="1120px">
        {PAINEIS.map((p) => (
          <PainelCard key={p.key} def={p} onClick={() => navigate(`/precos/${p.key}`)} />
        ))}
      </SimpleGrid>
    </Box>
  );
};
